import { useSnackbar } from "notistack";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import type { TranslationID } from "@citrine/lang/utils";

const ARCHIVE_RESOURCES = ["analysis", "search-space", "ai-model"] as const;
type ArchiveResourceType = (typeof ARCHIVE_RESOURCES)[number];
const isArchiveResourceType = (
  resourceType: string
): resourceType is ArchiveResourceType =>
  ARCHIVE_RESOURCES.includes(resourceType);

type ArchiveResourceAction = "create" | "update" | "archive" | "restore";

const CRUD_RESOURCES = [
  "analysis-plot",
  "api-key",
  "branch",
  "candidate",
  "candidate-filter-group",
  "candidate-table-stickied-selection",
  "catalyst-document",
  "data-query",
  "dataset",
  "experiment",
  "file",
  "ingest",
  "material",
  "project",
  "project-member",
  "team",
  "team-member",
  "training-set",
  "user",
  "user-request",
  "workspace",
] as const;
type CrudResourceType = (typeof CRUD_RESOURCES)[number];
const isCrudResourceType = (
  resourceType: string
): resourceType is CrudResourceType => CRUD_RESOURCES.includes(resourceType);

type CrudResourceAction = "create" | "update" | "delete";

export function useResourceNotifications<
  ResourceType extends ArchiveResourceType | CrudResourceType,
>(
  resourceType: ResourceType,
  resourceAction: ResourceType extends ArchiveResourceType
    ? ArchiveResourceAction
    : CrudResourceAction
) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  return useMemo(() => {
    const [successId, errorId]: [
      TranslationID | undefined,
      TranslationID | undefined,
    ] = isArchiveResourceType(resourceType)
      ? [
          `resources.${resourceType}.${resourceAction as ArchiveResourceAction}.success`,
          `resources.${resourceType}.${resourceAction as ArchiveResourceAction}.error`,
        ]
      : isCrudResourceType(resourceType)
        ? [
            `resources.${resourceType}.${resourceAction as CrudResourceAction}.success`,
            `resources.${resourceType}.${resourceAction as CrudResourceAction}.error`,
          ]
        : [undefined, undefined];

    return {
      onSuccess() {
        enqueueSnackbar(formatMessage({ id: successId }), {
          variant: "success",
        });
      },
      onError() {
        enqueueSnackbar(formatMessage({ id: errorId }), {
          variant: "error",
        });
      },
    };
  }, [enqueueSnackbar, formatMessage, resourceAction, resourceType]);
}
